import type { PublicConfig, Slot } from "./helpers.js";

/**
 * The booking page's only connection to the backend: the public `web-booking` Edge Function. No signed-in user, no table access.
 * Every failure becomes a BookingApiError whose `code` is one of the function's answers (see errorMessage in helpers.ts).
 */

const url = import.meta.env.VITE_SUPABASE_URL;
const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

export class BookingApiError extends Error {
  constructor(
    readonly code: string,
    readonly status: number,
    readonly detail?: string,
    readonly attemptsLeft?: number
  ) {
    super(code);
    this.name = "BookingApiError";
  }
}

async function post<T>(body: Record<string, unknown>): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${url}/functions/v1/web-booking`, {
      method: "POST",
      headers: { "content-type": "application/json", apikey: anonKey ?? "", authorization: `Bearer ${anonKey ?? ""}` },
      body: JSON.stringify(body),
    });
  } catch {
    throw new BookingApiError("network", 0);
  }
  const data = (await res.json().catch(() => ({}))) as { error?: string; detail?: string; attempts_left?: number };
  if (!res.ok) throw new BookingApiError(data.error ?? "unexpected_error", res.status, data.detail, data.attempts_left);
  return data as T;
}

export interface BookedVisit {
  booking_id: string;
  starts_at: string;
  ends_at: string;
  staff: string | null;
  services: string[];
  guest_name: string | null;
}

export interface MyBooking {
  id: string;
  guest_name: string | null;
  starts_at: string;
  ends_at: string;
  staff: string | null;
  resource_id: string;
  services: string[];
  service_ids: string[];
  can_change: boolean;
}

export type Purpose = "book" | "manage";

export const bookingApi = (slug: string) => ({
  config: () => post<PublicConfig>({ action: "config", slug }),
  slots: (serviceIds: string[], from: string, days = 14) =>
    post<{ slots: Slot[] }>({ action: "slots", slug, service_ids: serviceIds, from, days }).then((r) => r.slots),
  sendCode: (phone: string, purpose: Purpose, turnstileToken: string | null) =>
    post<{ ok: true }>({ action: "send_code", slug, phone, purpose, turnstile_token: turnstileToken }),
  verify: (phone: string, purpose: Purpose, code: string) =>
    post<{ ok: true; token: string; needs_name: boolean; name: string | null; dob_mode?: "off" | "verify" | "collect" }>({ action: "verify", slug, phone, purpose, code }),
  book: (token: string, b: { serviceIds: string[]; resourceId: string | null; startsAt: string; name: string | null; guestName: string | null; dob?: string | null; note?: string | null; customFields: Record<string, unknown> }) =>
    post<{ ok: true; booking: BookedVisit }>({
      action: "book", slug, token, service_ids: b.serviceIds, resource_id: b.resourceId, starts_at: b.startsAt,
      name: b.name, guest_name: b.guestName, dob: b.dob ?? null, note: b.note ?? null, custom_fields: b.customFields,
    }).then((r) => r.booking),
  /** The business checks dates of birth: check the one the customer typed against the one on file (3 wrong tries end the session). */
  verifyDob: (token: string, dob: string) => post<{ ok: true }>({ action: "verify_dob", slug, token, dob }),
  /** Read-only: the verified customer's own bookings, for the day view (works with a booking session). */
  myCalendar: (token: string) => post<{ bookings: MyBooking[] }>({ action: "my_calendar", slug, token }).then((r) => r.bookings),
  myBookings: (token: string) => post<{ bookings: MyBooking[] }>({ action: "my_bookings", slug, token }).then((r) => r.bookings),
  cancel: (token: string, bookingId: string) => post<{ ok: true }>({ action: "cancel", slug, token, booking_id: bookingId }),
  reschedule: (token: string, bookingId: string, startsAt: string, resourceId: string | null) =>
    post<{ ok: true; starts_at: string; ends_at: string }>({ action: "reschedule", slug, token, booking_id: bookingId, starts_at: startsAt, resource_id: resourceId }),
});

export type BookingApi = ReturnType<typeof bookingApi>;
